import { useQuery } from '@tanstack/react-query';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { VisitDetails, VisitStory, visitDay, type VisitStep } from '../PersonVisits';
import { fetchMyVisit } from '../memberContactsApi';
import { dayOf } from '../visitorAccessFormat';
import { ListSheet } from './ListSheet';

/**
 * What a tapped row opens: the PIN's visit when the row has one, else what the gate recorded. The
 * gate's own steps also stand in while a visit can't be loaded.
 */
export interface VisitTarget {
  /** The day the drawer is titled with until the visit loads. */
  day: string;
  inviteId?: string;
  steps: VisitStep[];
  details: [string, string | null][];
}

/** A visit's drawer, as a person's page opens one: the day, what happened, then the PIN's details. */
export function VisitSheet({
  target,
  open,
  onOpenChange,
}: {
  target: VisitTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const inviteId = target?.inviteId;
  const visitQuery = useQuery({
    queryKey: ['access-invite-visit', inviteId],
    queryFn: () => fetchMyVisit(inviteId!),
    enabled: open && Boolean(inviteId),
  });
  const visit = inviteId ? visitQuery.data : undefined;

  return (
    <ListSheet open={open} onOpenChange={onOpenChange} title={target ? dayOf(visit ? visitDay(visit) : target.day) : ''}>
      {!target ? null : visit ? (
        <VisitDetails visit={visit} />
      ) : inviteId && visitQuery.isLoading ? (
        <Skeleton className="h-60 rounded-[16px]" />
      ) : visitQuery.isError && !target.steps.length ? (
        <ErrorState onRetry={() => visitQuery.refetch()} />
      ) : (
        <VisitStory steps={target.steps} details={target.details} />
      )}
    </ListSheet>
  );
}
